/**
 * Sidebar colour stripes (backlog #44): a 3px left-edge stripe on chat sections and channels, no background fills.
 * People pick their own colour (right-click > Colour, or channel settings); people who organise chat sections
 * also set the workspace default. The brand yellow is never in the palette: it stays reserved for active and unread.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, Palette, RotateCcw, Ban } from 'lucide-react';
import { MOVE_DOWN_LABEL, MOVE_UP_LABEL, SIDEBAR_COLOURS, resolveSidebarColour, sidebarColour, type SidebarColourChoice, type SidebarColourId, type SidebarColourTarget, type SidebarColours } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { useChatSections, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';

type SectionsData = Awaited<ReturnType<typeof api.chatSections>>;

export function useSidebarColour(type: SidebarColourTarget, id: string) {
  const ws = useWorkspaceId();
  const sections = useChatSections();
  const toast = useUi(s => s.toast);
  const resolved = resolveSidebarColour(sections.data?.colours, type, id);
  const apply = (colours: SidebarColours) => queryClient.setQueryData<SectionsData>(qk.chatSections(ws ?? ''), d => (d ? { ...d, colours } : d));
  return {
    ...resolved,
    canManage: Boolean(sections.data?.can_manage),
    setMine: async (colour: SidebarColourChoice | null) => {
      try { apply(await api.setMySidebarColour(ws!, { target_type: type, target_id: id, colour })); } catch (e) { toast(errorMessage(e), 'error'); }
    },
    setDefault: async (colour: SidebarColourId | null) => {
      try {
        apply(await api.setDefaultSidebarColour(ws!, { target_type: type, target_id: id, colour }));
        toast(colour ? `Workspace colour set to ${sidebarColour(colour)?.name}` : 'Workspace colour removed', 'success');
      } catch (e) { toast(errorMessage(e), 'error'); }
    },
    /** t449u: for dialogs that save on Save: these throw, so the dialog can show the error and stay open. */
    putMine: async (colour: SidebarColourChoice | null) => { apply(await api.setMySidebarColour(ws!, { target_type: type, target_id: id, colour })); },
    putDefault: async (colour: SidebarColourId | null) => { apply(await api.setDefaultSidebarColour(ws!, { target_type: type, target_id: id, colour })); },
  };
}

/** Pending colour edits in a dialog: a key is present only when it was changed. */
export type SidebarColourDraft = { mine?: SidebarColourChoice | null; def?: SidebarColourId | null };

/** The stripe itself. Sits inside the item's left edge, so the active indicator (at the rail edge) never hides it. */
export function Stripe({ hex, className }: { hex: string | undefined; className?: string }) {
  if (!hex) return null;
  return <span aria-hidden data-testid="sidebar-stripe" className={clsx('pointer-events-none absolute inset-y-1 w-[3px] rounded-full', className ?? 'left-0')} style={{ background: hex }} />;
}

export function Swatch({ hex, className }: { hex?: string | null; className?: string }) {
  return <span aria-hidden className={clsx('inline-block h-3 w-3 shrink-0 rounded-full ring-1 ring-black/20 dark:ring-white/20', !hex && 'bg-transparent ring-slate-400', className)} style={hex ? { background: hex } : undefined} />;
}

/** Radio-style palette. Each option's accessible name is the colour name. */
export function ColourOptions({ value, onPick, extra, label }: { value: string | null; onPick: (id: SidebarColourId) => void; extra?: ReactNode; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-1 sm:grid-cols-5">
      {SIDEBAR_COLOURS.map(c => (
        <button key={c.id} type="button" role="radio" aria-checked={value === c.id} onClick={() => onPick(c.id)}
          className={clsx('flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700', value === c.id && 'ring-1 ring-inset ring-slate-900 dark:ring-white')}>
          <Swatch hex={c.hex} />{c.name}{value === c.id && <Check className="ml-auto h-3.5 w-3.5" />}
        </button>
      ))}
      {extra}
    </div>
  );
}

/** Your colour + (for organisers) the workspace default, as used in channel settings. */
export function SidebarColourSettings({ type, id, draft, onDraft }: { type: SidebarColourTarget; id: string; draft?: SidebarColourDraft; onDraft?: (d: SidebarColourDraft) => void }) {
  const c = useSidebarColour(type, id);
  const def = sidebarColour(c.workspaceDefault);
  // t449u: with onDraft the choices are kept until the dialog's Save; without it they save straight away.
  const mine = draft && 'mine' in draft ? draft.mine ?? null : c.mine;
  const workspace = draft && 'def' in draft ? draft.def ?? null : c.workspaceDefault;
  const setMine = (v: SidebarColourChoice | null) => (onDraft ? onDraft({ ...draft, mine: v }) : void c.setMine(v));
  const setDefault = (v: SidebarColourId | null) => (onDraft ? onDraft({ ...draft, def: v }) : void c.setDefault(v));
  return (
    <div className="space-y-5">
      <div>
        <h3 className="mb-1 text-sm font-semibold">Your colour</h3>
        <p className="mb-2 text-xs text-slate-500">A thin stripe on the left of this {type === 'section' ? 'section' : 'channel'} in your sidebar. Only you see your choice.{type === 'conversation' ? ' Without a colour of its own, a channel in a coloured section shows the section\'s stripe.' : ''}</p>
        <ColourOptions label="Your colour" value={mine === 'none' ? null : mine} onPick={id => setMine(id)} />
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <button type="button" className="rounded-md px-2 py-1 ring-1 ring-slate-300 hover:bg-slate-100 dark:ring-slate-600 dark:hover:bg-slate-700" aria-pressed={mine === 'none'} onClick={() => setMine('none')}>No colour</button>
          <button type="button" disabled={!mine} className="rounded-md px-2 py-1 ring-1 ring-slate-300 hover:bg-slate-100 disabled:opacity-50 dark:ring-slate-600 dark:hover:bg-slate-700" onClick={() => setMine(null)}>
            Reset to workspace colour{workspace ? ` (${sidebarColour(workspace)?.name ?? def?.name})` : ' (none)'}
          </button>
        </div>
      </div>
      {c.canManage && (
        <div>
          <h3 className="mb-1 text-sm font-semibold">Workspace colour</h3>
          <p className="mb-2 text-xs text-slate-500">The default everyone sees. People can still choose their own.</p>
          <ColourOptions label="Workspace colour" value={workspace} onPick={id => setDefault(id)} />
          <button type="button" disabled={!workspace} className="mt-2 rounded-md px-2 py-1 text-xs ring-1 ring-slate-300 hover:bg-slate-100 disabled:opacity-50 dark:ring-slate-600 dark:hover:bg-slate-700" onClick={() => setDefault(null)}>Remove workspace colour</button>
        </div>
      )}
    </div>
  );
}

export interface ColourMenuTarget { type: SidebarColourTarget; id: string; name: string; x: number; y: number; /** DMs are personal: no workspace colour. */ personalOnly?: boolean;
  /** t446u: Move up / Move down within its section, when the channel can move that way. */ order?: { up?: () => void; down?: () => void } }

/** Right-click menu for a sidebar section or channel: Colour (yours) and, for organisers, Workspace colour. */
export function SidebarColourMenu({ target, onClose }: { target: ColourMenuTarget; onClose: () => void }) {
  const c = useSidebarColour(target.type, target.id);
  const [open, setOpen] = useState<'mine' | 'default' | null>('mine');
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: target.x, top: target.y });
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: Math.min(target.x, window.innerWidth - r.width - 8), top: Math.max(8, Math.min(target.y, window.innerHeight - r.height - 8)) });
  }, [target.x, target.y, open]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) closeRef.current(); };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => window.addEventListener('mousedown', onDown), 0);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onDown); };
  }, []);
  const current = c.colour ? `${c.colour.name}${c.source === 'default' ? ' (workspace)' : ''}` : 'None';
  const def = sidebarColour(c.workspaceDefault);
  const pick = (fn: () => Promise<void>) => { void fn(); onClose(); };
  const item = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none dark:text-slate-200 dark:hover:bg-slate-700 dark:focus:bg-slate-700';
  const list = (value: string | null, onPick: (id: SidebarColourId) => void, scope: string) => SIDEBAR_COLOURS.map(col => (
    <button key={col.id} type="button" role="menuitemradio" aria-checked={value === col.id} className={clsx(item, 'pl-7')} onClick={() => onPick(col.id)}
      aria-label={`${scope}: ${col.name}${value === col.id ? ' (current)' : ''}`}>
      <Swatch hex={col.hex} />{col.name}{value === col.id && <Check className="ml-auto h-3.5 w-3.5" />}
    </button>
  ));
  return createPortal(
    <div ref={ref} role="menu" aria-label={`${target.name} options`} data-testid="sidebar-colour-menu" style={{ left: pos.left, top: pos.top }}
      className="on-page fixed z-50 max-h-[80vh] w-60 overflow-y-auto rounded-lg bg-white py-1 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
      <div className="truncate px-3 pb-1 pt-1.5 text-xs font-semibold text-slate-500">{target.type === 'section' ? 'Section' : 'Channel'}: {target.name}</div>
      {(target.order?.up || target.order?.down) && <>
        {target.order.up && <button type="button" role="menuitem" className={item} data-testid="channel-move-up" onClick={() => { target.order!.up!(); onClose(); }}><ArrowUp className="h-4 w-4" />{MOVE_UP_LABEL}</button>}
        {target.order.down && <button type="button" role="menuitem" className={item} data-testid="channel-move-down" onClick={() => { target.order!.down!(); onClose(); }}><ArrowDown className="h-4 w-4" />{MOVE_DOWN_LABEL}</button>}
        <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
      </>}
      <button type="button" role="menuitem" aria-expanded={open === 'mine'} className={item} onClick={() => setOpen(open === 'mine' ? null : 'mine')}>
        <Palette className="h-4 w-4" />Colour: {current}<span className="ml-auto">{open === 'mine' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
      </button>
      {open === 'mine' && <>
        {list(c.mine === 'none' ? null : c.mine ?? null, id => pick(() => c.setMine(id)), 'Colour')}
        <button type="button" role="menuitemradio" aria-checked={c.mine === 'none'} className={clsx(item, 'pl-7')} onClick={() => pick(() => c.setMine('none'))}><Ban className="h-3 w-3" />No colour</button>
        {c.mine && <button type="button" role="menuitem" className={clsx(item, 'pl-7')} onClick={() => pick(() => c.setMine(null))}><RotateCcw className="h-3 w-3" />Reset to workspace colour ({def?.name ?? 'none'})</button>}
      </>}
      {c.canManage && !target.personalOnly && <>
        <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
        <button type="button" role="menuitem" aria-expanded={open === 'default'} className={item} onClick={() => setOpen(open === 'default' ? null : 'default')}>
          <Palette className="h-4 w-4" />Workspace colour: {def?.name ?? 'None'}<span className="ml-auto">{open === 'default' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
        </button>
        {open === 'default' && <>
          {list(c.workspaceDefault, id => pick(() => c.setDefault(id)), 'Workspace colour')}
          {c.workspaceDefault && <button type="button" role="menuitem" className={clsx(item, 'pl-7')} onClick={() => pick(() => c.setDefault(null))}><Ban className="h-3 w-3" />Remove workspace colour</button>}
        </>}
      </>}
    </div>,
    document.body,
  );
}
